import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { Modal, toast } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { ObjectsService } from "app/data/services";
import type { MaintenanceAlertRule } from "components/common/objects/MaintenanceAlerts/maintenanceAlertRuleTypes";

type ObjectOption = {
  uniqueId: string;
  name: string;
};

export type MaintenanceAlertRuleModalData = {
  uniqueId: string;
  name: string;
  description?: string;
  metric: number;
  firstTrigger: number;
  interval: number;
  recipients: string[];
  objectIds: string[];
};

type AddMaintenanceAlertRuleModalProps = {
  open: boolean;
  onClose: () => void;
  accountId: string;
  defaultObject?: ObjectOption | null;
  mode?: "create" | "edit";
  initialRule?: MaintenanceAlertRuleModalData | null;
  /**
   * When true, new rules open with "Select all objects" checked by default.
   * The user can still uncheck it to pick objects individually.
   */
  defaultSelectAllObjects?: boolean;
  onSuccess?: (result: MaintenanceAlertRuleSaveResult) => void;
};

export type MaintenanceAlertRuleSaveResult = {
  action: "create" | "update";
  rule: MaintenanceAlertRule;
};

type FormValues = {
  name: string;
  recipients: string[];
  objectIds: string[];
  selectAllObjects: boolean;
  metric: number | "";
  firstTrigger: string;
  intervalOption: string;
  customInterval: string;
};

const toDigits = (value: string) => value.replace(/\D/g, "");

const formatWithSpaces = (value: string) => {
  const digits = toDigits(value);
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** Keeps manual TextField labels aligned with core Formik fields */
const MODAL_INPUT_LABEL_SX = {
  fontSize: 14,
  fontWeight: 500
} as const;

/** Shared outlined field look — all inputs use native MUI TextField (or equivalent outline) */
const MODAL_TEXT_FIELD_SX = {
  "& .MuiInputLabel-root": MODAL_INPUT_LABEL_SX,
  "& .MuiFormHelperText-root": {
    marginLeft: 0
  }
} as const;

/**
 * Outlined Select with empty string value: without shrink, the label sits on the
 * border and overlaps the placeholder "Select one" text.
 */
const MODAL_SELECT_INPUT_LABEL_PROPS = {
  shrink: true,
  sx: MODAL_INPUT_LABEL_SX
} as const;

const isAllObjectsRuleSelection = (
  objectIds: string[] | undefined,
  allAccountObjects: ObjectOption[]
) =>
  objectIds?.length === 0 ||
  (objectIds?.length === allAccountObjects.length &&
    allAccountObjects.length > 0);

const tryCommitEmailInput = (
  rawInput: string,
  currentRecipients: string[],
  setFieldValue: (field: string, value: unknown) => void,
  setFieldTouched: (field: string, touched?: boolean) => void,
  invalidMessage: string
): boolean => {
  const input = rawInput.trim();
  if (!input) return false;

  if (!isValidEmail(input)) {
    toast.error(invalidMessage);
    return false;
  }

  if (!currentRecipients.includes(input)) {
    setFieldValue("recipients", [...currentRecipients, input]);
  }
  setFieldTouched("recipients", true);
  return true;
};

const MODAL_FIELDS_STACK_SPACING = 3;

const AddMaintenanceAlertRuleModal: FC<AddMaintenanceAlertRuleModalProps> = ({
  open,
  onClose,
  accountId,
  defaultObject,
  mode = "create",
  initialRule,
  defaultSelectAllObjects = false,
  onSuccess
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const [allAccountObjects, setAllAccountObjects] = useState<ObjectOption[]>(
    []
  );
  const [isLoadingAllObjects, setIsLoadingAllObjects] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [shouldFocusCustomInterval, setShouldFocusCustomInterval] =
    useState(false);
  const customIntervalInputRef = useRef<HTMLInputElement>(null);
  const metricOptions = useMemo(
    () => [
      { value: 1, name: t("maintenanceAlerts.contactHours") },
      { value: 2, name: t("maintenanceAlerts.runtimeHours") }
    ],
    [t]
  );
  const intervalOptions = useMemo(
    () => [
      { value: "custom", name: t("maintenanceAlerts.custom") },
      { value: "300", name: "300 h" },
      { value: "500", name: "500 h" },
      { value: "1000", name: "1000 h" },
      { value: "2000", name: "2000 h" }
    ],
    [t]
  );

  useEffect(() => {
    if (!open) {
      setEmailInput("");
      setShouldFocusCustomInterval(false);
    }
  }, [open]);

  useEffect(() => {
    if (!shouldFocusCustomInterval || !open) return;

    const frameId = requestAnimationFrame(() => {
      customIntervalInputRef.current?.focus();
      customIntervalInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      setShouldFocusCustomInterval(false);
    });

    return () => cancelAnimationFrame(frameId);
  }, [shouldFocusCustomInterval, open]);

  useEffect(() => {
    const fetchAllObjectsBasicInfo = async () => {
      if (!open || !accountId) return;
      try {
        setIsLoadingAllObjects(true);
        const response = await ObjectsService.getAllBasicInfoObjects(accountId);
        const fetchedObjects = response?.data || [];
        setAllAccountObjects(fetchedObjects);
      } catch {
        toast.error(t("maintenanceAlerts.toasts.loadObjectsError"));
      } finally {
        setIsLoadingAllObjects(false);
      }
    };

    fetchAllObjectsBasicInfo();
  }, [open, accountId, t]);

  /**
   * We need the full account objects list before computing initial values when:
   * - creating a rule with `defaultSelectAllObjects` (to seed `objectIds`); or
   * - editing any rule (to detect if it's an "all objects" rule).
   */
  const needsAllObjectsForFormInit =
    open && ((mode === "create" && defaultSelectAllObjects) || mode === "edit");

  const isFormReady =
    !open || !needsAllObjectsForFormInit || !isLoadingAllObjects;

  /**
   * Includes `allAccountObjects.length` so Formik remounts and recomputes initial
   * values once the async fetch resolves (otherwise edit-mode "all objects" rules
   * could keep the wrong `selectAllObjects` flag after the data arrives).
   */
  const formKey = `${mode}-${
    initialRule?.uniqueId ?? "create"
  }-${allAccountObjects.length}`;

  const buildInitialValues = (): FormValues => {
    const selectAllObjects = Boolean(
      initialRule
        ? isAllObjectsRuleSelection(initialRule.objectIds, allAccountObjects)
        : defaultSelectAllObjects
    );

    const defaultObjectIds = defaultObject?.uniqueId
      ? [defaultObject.uniqueId]
      : [];

    const objectIds = selectAllObjects
      ? allAccountObjects.map((item) => item.uniqueId)
      : initialRule?.objectIds || defaultObjectIds;

    return {
      name: initialRule?.name || "",
      recipients: initialRule?.recipients || [],
      objectIds,
      selectAllObjects,
      metric: initialRule?.metric || "",
      firstTrigger: initialRule?.firstTrigger
        ? formatWithSpaces(String(initialRule.firstTrigger))
        : "",
      intervalOption: initialRule?.interval
        ? intervalOptions.some(
            (option) => option.value === String(initialRule.interval)
          )
          ? String(initialRule.interval)
          : "custom"
        : "",
      customInterval:
        initialRule?.interval &&
        !intervalOptions.some(
          (option) => option.value === String(initialRule.interval)
        )
          ? formatWithSpaces(String(initialRule.interval))
          : ""
    };
  };

  const initialValues: FormValues = useMemo(
    () => buildInitialValues(),
    [formKey]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .required(t("maintenanceAlerts.validation.nameRequired")),
        recipients: Yup.array()
          .of(
            Yup.string().test(
              "email",
              t("maintenanceAlerts.validation.emailInvalid"),
              (v) => (v ? isValidEmail(v) : false)
            )
          )
          .min(1, t("maintenanceAlerts.validation.recipientsRequired")),
        selectAllObjects: Yup.boolean(),
        objectIds: Yup.array().when("selectAllObjects", {
          is: false,
          then: (schema) =>
            schema.min(1, t("maintenanceAlerts.validation.objectsRequired")),
          otherwise: (schema) => schema
        }),
        metric: Yup.mixed<number | "">()
          .test(
            "required",
            t("maintenanceAlerts.validation.metricRequired"),
            (value) => value !== "" && value !== null && value !== undefined
          )
          .test(
            "valid-metric",
            t("maintenanceAlerts.validation.metricRequired"),
            (value) => {
              if (value === "" || value == null) return true;
              const metric = Number(value);
              return metric === 1 || metric === 2;
            }
          ),
        firstTrigger: Yup.string()
          .required(t("maintenanceAlerts.validation.firstTriggerRequired"))
          .test(
            "positive-number",
            t("maintenanceAlerts.validation.firstTriggerPositive"),
            (value) => Number(toDigits(value || "")) > 0
          ),
        intervalOption: Yup.string().required(
          t("maintenanceAlerts.validation.intervalRequired")
        ),
        customInterval: Yup.string().when("intervalOption", {
          is: "custom",
          then: (schema) =>
            schema
              .required(
                t("maintenanceAlerts.validation.customIntervalRequired")
              )
              .test(
                "positive-custom",
                t("maintenanceAlerts.validation.intervalPositive"),
                (value) => Number(toDigits(value || "")) > 0
              ),
          otherwise: (schema) => schema
        })
      }),
    [t]
  );

  const submitHandler = async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>
  ) => {
    try {
      const intervalValue =
        values.intervalOption === "custom"
          ? Number(toDigits(values.customInterval))
          : Number(values.intervalOption);

      const payload = {
        name: values.name.trim(),
        description: initialRule?.description || "",
        metric: Number(values.metric),
        firstTrigger: Number(toDigits(values.firstTrigger)),
        interval: intervalValue,
        recipients: values.recipients,
        selectAllObjects: values.selectAllObjects,
        objectIds: values.selectAllObjects ? [] : values.objectIds
      };

      const response =
        mode === "edit" && initialRule?.uniqueId
          ? await ObjectsService.patchMaintenanceAlertRule(
              accountId,
              initialRule.uniqueId,
              {
                uniqueId: initialRule.uniqueId,
                ...payload
              }
            )
          : await ObjectsService.postMaintenanceAlertRule(accountId, payload);

      const apiRule = response?.data;
      const savedRule: MaintenanceAlertRule = {
        uniqueId:
          apiRule?.uniqueId ??
          initialRule?.uniqueId ??
          `temp-${Date.now().toString(36)}`,
        name: apiRule?.name ?? payload.name,
        description: apiRule?.description ?? payload.description,
        metric: apiRule?.metric ?? payload.metric,
        firstTrigger: apiRule?.firstTrigger ?? payload.firstTrigger,
        interval: apiRule?.interval ?? payload.interval,
        recipients: apiRule?.recipients ?? payload.recipients,
        selectAllObjects: apiRule?.selectAllObjects ?? payload.selectAllObjects,
        objectIds: apiRule?.objectIds ?? payload.objectIds
      };

      toast.success(
        mode === "edit"
          ? t("maintenanceAlerts.toasts.updateSuccess")
          : t("maintenanceAlerts.toasts.createSuccess")
      );
      onSuccess?.({
        action: mode === "edit" ? "update" : "create",
        rule: savedRule
      });
      onClose();
      formikHelpers.resetForm();
    } catch {
      toast.error(
        mode === "edit"
          ? t("maintenanceAlerts.toasts.updateError")
          : t("maintenanceAlerts.toasts.createError")
      );
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      validateOnMount
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        isValid,
        setFieldValue,
        setFieldTouched,
        handleSubmit
      }) => {
        const selectedObjectOptions = allAccountObjects.filter((item) =>
          values.objectIds.includes(item.uniqueId)
        );

        const trimmedEmailInput = emailInput.trim();
        const hasPendingValidEmail =
          trimmedEmailInput.length > 0 && isValidEmail(trimmedEmailInput);
        const showRecipientsError =
          values.recipients.length === 0 &&
          touched.recipients &&
          Boolean(errors.recipients) &&
          !hasPendingValidEmail;

        const handleCommitEmailInput = () => {
          const committed = tryCommitEmailInput(
            emailInput,
            values.recipients,
            setFieldValue,
            setFieldTouched,
            t("maintenanceAlerts.validation.emailInvalid")
          );
          if (committed) {
            setEmailInput("");
          }
        };

        const handleSelectAllObjectsChange = (checked: boolean) => {
          setFieldValue("selectAllObjects", checked);
          if (checked) {
            setFieldValue(
              "objectIds",
              allAccountObjects.map((item) => item.uniqueId)
            );
          } else {
            setFieldValue("objectIds", []);
          }
          setFieldTouched("objectIds", true);
        };

        const autocompleteValue = selectedObjectOptions;

        return (
          <Modal
            open={open}
            onClose={onClose}
            title={t(
              mode === "edit"
                ? "maintenanceAlerts.editAlertRule"
                : "maintenanceAlerts.addAlertRule"
            )}
            theme={theme}
            size="customSize"
            secondaryBtnProps={{
              text: t("buttonsText.cancel"),
              onClick: onClose
            }}
            primaryBtnProps={{
              text:
                mode === "edit"
                  ? t("buttonsText.update")
                  : t("buttonsText.save"),
              type: "submit",
              onClick: handleSubmit,
              loading: isSubmitting,
              disabled: !isValid || !isFormReady
            }}
          >
            {!isFormReady ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Stack spacing={MODAL_FIELDS_STACK_SPACING}>
                <Box display="flex" justifyContent="center">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "12px",
                      backgroundColor: "#ECFDF3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <NotificationsNoneRoundedIcon
                      sx={{ color: "#00B2A3", fontSize: 30 }}
                    />
                  </Box>
                </Box>
                <Typography fontSize={20} fontWeight={600} textAlign="center">
                  {t(
                    mode === "edit"
                      ? "maintenanceAlerts.editAlertRule"
                      : "maintenanceAlerts.addAlertRule"
                  )}
                </Typography>
                <Typography fontSize={14} color="#646D7B" textAlign="center">
                  {t("maintenanceAlerts.modalDescription")}
                </Typography>
                {values.selectAllObjects ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      backgroundColor: "#EFF8FF",
                      border: "1px solid #B2DDFF",
                      borderRadius: "12px",
                      px: 1.5,
                      py: 1
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoOutlinedIcon
                        sx={{ color: "#175CD3", fontSize: 18 }}
                      />
                      <Typography fontSize={13} color="#175CD3">
                        {t("maintenanceAlerts.allObjectsNotice")}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}

                <TextField
                  fullWidth
                  required
                  margin="none"
                  variant="outlined"
                  name="name"
                  label={t("maintenanceAlerts.maintenanceAlertName")}
                  placeholder={t(
                    "maintenanceAlerts.maintenanceAlertNamePlaceholder"
                  )}
                  value={values.name}
                  onChange={(event) =>
                    setFieldValue("name", event.target.value)
                  }
                  onBlur={() => setFieldTouched("name", true)}
                  error={touched.name && Boolean(errors.name)}
                  helperText={
                    touched.name && errors.name ? String(errors.name) : ""
                  }
                  sx={MODAL_TEXT_FIELD_SX}
                />

                <Box>
                  <TextField
                    fullWidth
                    required
                    margin="none"
                    variant="outlined"
                    name="recipients-input"
                    label={t("maintenanceAlerts.recipientsLabel")}
                    placeholder={t("maintenanceAlerts.recipientsPlaceholder")}
                    value={emailInput}
                    onChange={(event) => {
                      setEmailInput(event.target.value);
                      const next = event.target.value.trim();
                      if (
                        next.length > 0 &&
                        (isValidEmail(next) || values.recipients.length > 0)
                      ) {
                        setFieldTouched("recipients", false);
                      }
                    }}
                    onBlur={() => {
                      handleCommitEmailInput();
                      if (values.recipients.length === 0) {
                        setFieldTouched("recipients", true);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== ",") return;
                      event.preventDefault();
                      handleCommitEmailInput();
                    }}
                    helperText={
                      showRecipientsError
                        ? String(errors.recipients)
                        : t("maintenanceAlerts.recipientsHint")
                    }
                    error={showRecipientsError}
                    sx={MODAL_TEXT_FIELD_SX}
                  />
                  <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
                    {values.recipients.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() =>
                          setFieldValue(
                            "recipients",
                            values.recipients.filter((item) => item !== email)
                          )
                        }
                      />
                    ))}
                  </Stack>
                </Box>

                <FormControlLabel
                  sx={{
                    m: 0,
                    mr: 0,
                    ml: 0,
                    alignItems: "center",
                    gap: 0.75,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.43,
                      paddingTop: 0
                    }
                  }}
                  control={
                    <Checkbox
                      size="small"
                      sx={{ p: 0.5 }}
                      checked={values.selectAllObjects}
                      onChange={(event) =>
                        handleSelectAllObjectsChange(event.target.checked)
                      }
                    />
                  }
                  label={t("formFieldsTranslation:selectAllObjects.label")}
                />

                {!values.selectAllObjects ? (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={allAccountObjects}
                    loading={isLoadingAllObjects}
                    value={autocompleteValue}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.uniqueId === value.uniqueId
                    }
                    onChange={(_, newValue) => {
                      setFieldValue(
                        "objectIds",
                        newValue.map((item) => item.uniqueId)
                      );
                      setFieldTouched("objectIds", true);
                    }}
                    onBlur={() => setFieldTouched("objectIds", true)}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.uniqueId}>
                        <Checkbox
                          size="small"
                          sx={{ mr: 1 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    )}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const { key, ...chipProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={option.name}
                            size="small"
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        margin="none"
                        variant="outlined"
                        name="objects"
                        label={t("formFieldsTranslation:objects.label")}
                        placeholder={t("maintenanceAlerts.objectsPlaceholder")}
                        error={touched.objectIds && Boolean(errors.objectIds)}
                        helperText={
                          touched.objectIds && errors.objectIds
                            ? String(errors.objectIds)
                            : t("maintenanceAlerts.objectsHint")
                        }
                        sx={MODAL_TEXT_FIELD_SX}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": {
                        flexWrap: "wrap"
                      }
                    }}
                  />
                ) : null}

                <TextField
                  select
                  fullWidth
                  required
                  margin="none"
                  variant="outlined"
                  name="metric"
                  label={t("tableHeadingsTranslation:tableHeading.metric")}
                  value={values.metric}
                  onChange={(event) => {
                    const raw = event.target.value;
                    setFieldValue("metric", raw === "" ? "" : Number(raw));
                    if (raw !== "") {
                      setFieldTouched("metric", false);
                    }
                  }}
                  onBlur={() => setFieldTouched("metric", true)}
                  error={touched.metric && Boolean(errors.metric)}
                  helperText={
                    touched.metric && errors.metric ? String(errors.metric) : ""
                  }
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === "" || selected === undefined) {
                        return (
                          <Typography
                            component="span"
                            color="text.secondary"
                            sx={{ fontSize: "1rem" }}
                          >
                            {t("maintenanceAlerts.selectOne")}
                          </Typography>
                        );
                      }
                      const option = metricOptions.find(
                        (item) =>
                          item.value === selected ||
                          item.value === Number(selected)
                      );
                      return option?.name ?? String(selected);
                    }
                  }}
                  InputLabelProps={MODAL_SELECT_INPUT_LABEL_PROPS}
                  sx={MODAL_TEXT_FIELD_SX}
                >
                  <MenuItem value="">
                    <em>{t("maintenanceAlerts.selectOne")}</em>
                  </MenuItem>
                  {metricOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  required
                  margin="none"
                  variant="outlined"
                  name="firstTrigger"
                  label={t("maintenanceAlerts.firstTriggerLabel")}
                  value={values.firstTrigger}
                  onChange={(event) =>
                    setFieldValue(
                      "firstTrigger",
                      formatWithSpaces(event.target.value)
                    )
                  }
                  onBlur={() => setFieldTouched("firstTrigger", true)}
                  placeholder={t("maintenanceAlerts.firstTriggerPlaceholder")}
                  InputProps={{
                    endAdornment: <Typography color="#646D7B">h</Typography>
                  }}
                  helperText={
                    touched.firstTrigger && errors.firstTrigger
                      ? String(errors.firstTrigger)
                      : ""
                  }
                  error={!!(touched.firstTrigger && errors.firstTrigger)}
                  sx={MODAL_TEXT_FIELD_SX}
                />

                <TextField
                  select
                  fullWidth
                  required
                  margin="none"
                  variant="outlined"
                  name="intervalOption"
                  label={t("tableHeadingsTranslation:tableHeading.interval")}
                  value={values.intervalOption}
                  onChange={(event) => {
                    const nextInterval = event.target.value;
                    setFieldValue("intervalOption", nextInterval);
                    if (nextInterval === "custom") {
                      setShouldFocusCustomInterval(true);
                    }
                  }}
                  onBlur={() => setFieldTouched("intervalOption", true)}
                  error={
                    touched.intervalOption && Boolean(errors.intervalOption)
                  }
                  helperText={
                    touched.intervalOption && errors.intervalOption
                      ? String(errors.intervalOption)
                      : ""
                  }
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === "" || selected === undefined) {
                        return (
                          <Typography
                            component="span"
                            color="text.secondary"
                            sx={{ fontSize: "1rem" }}
                          >
                            {t("maintenanceAlerts.selectOne")}
                          </Typography>
                        );
                      }
                      const option = intervalOptions.find(
                        (item) => item.value === selected
                      );
                      return option?.name ?? String(selected);
                    }
                  }}
                  InputLabelProps={MODAL_SELECT_INPUT_LABEL_PROPS}
                  sx={MODAL_TEXT_FIELD_SX}
                >
                  <MenuItem value="">
                    <em>{t("maintenanceAlerts.selectOne")}</em>
                  </MenuItem>
                  {intervalOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>

                {values.intervalOption === "custom" && (
                  <TextField
                    fullWidth
                    required
                    margin="none"
                    variant="outlined"
                    name="customInterval"
                    inputRef={customIntervalInputRef}
                    label={t("maintenanceAlerts.customIntervalLabel")}
                    value={values.customInterval}
                    onChange={(event) =>
                      setFieldValue(
                        "customInterval",
                        formatWithSpaces(event.target.value)
                      )
                    }
                    onBlur={() => setFieldTouched("customInterval", true)}
                    placeholder={t(
                      "maintenanceAlerts.customIntervalPlaceholder"
                    )}
                    InputProps={{
                      endAdornment: <Typography color="#646D7B">h</Typography>
                    }}
                    helperText={
                      touched.customInterval && errors.customInterval
                        ? String(errors.customInterval)
                        : ""
                    }
                    error={!!(touched.customInterval && errors.customInterval)}
                    sx={MODAL_TEXT_FIELD_SX}
                  />
                )}
              </Stack>
            )}
          </Modal>
        );
      }}
    </Formik>
  );
};

export default AddMaintenanceAlertRuleModal;
